import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import elAlssonLogo from '../../assets/images/el-alsson-logo.png';
import { useAuth } from '../../contexts/AuthContext';
import './Sidebar.css';

/**
 * Menu items — items with `divider: true` render a section label, not a link.
 * Items with no permission gate are visible to everyone authenticated.
 */
const MENU_ITEMS = [
  { path: '/dashboard',        label: 'Dashboard',        icon: 'fas fa-tachometer-alt' },
  { path: '/chart-of-accounts', label: 'Chart of Accounts', icon: 'fas fa-book',                 permission: 'accounts.view' },
  { path: '/vouchers',         label: 'Vouchers',         icon: 'fas fa-file-invoice-dollar',    permission: 'vouchers.view' },
  { path: '/reports',          label: 'Reports',          icon: 'fas fa-chart-bar',              permission: 'reports.view' },

  // Purchasing section
  { divider: true, label: 'Purchasing', anyPermission: ['vendors.view', 'purchases.pr.view', 'purchases.po.view', 'purchases.invoice.view'] },
  { path: '/vendors',              label: 'Vendors',           icon: 'fas fa-building',        permission: 'vendors.view' },
  { path: '/purchasing/requests',  label: 'Purchase Requests', icon: 'fas fa-clipboard-list',  permission: 'purchases.pr.view' },
  { path: '/purchasing/orders',    label: 'Purchase Orders',   icon: 'fas fa-file-invoice',    permission: 'purchases.po.view' },
  { path: '/purchasing/invoices',  label: 'Purchase Invoices', icon: 'fas fa-receipt',         permission: 'purchases.invoice.view' },

  // Cash & Banks section
  { divider: true, label: 'Cash & Banks', anyPermission: ['cash_banks.view', 'cheques.view'] },
  { path: '/cash-banks/closing',      label: 'Daily Closing',    icon: 'fas fa-lock',               permission: 'cash_banks.view' },
  { path: '/cash-banks/daily-report', label: 'Daily Report',     icon: 'fas fa-chart-line',         permission: 'cash_banks.view' },
  { path: '/cash-banks/cheques',      label: 'Cheques',          icon: 'fas fa-money-check',        permission: 'cheques.view' },

  // Admin section — only rendered if user has at least one admin permission
  { divider: true, label: 'Administration', anyPermission: ['users.view','roles.view','entities.view','currencies.view','periods.view'] },
  { path: '/admin/users',      label: 'Users',            icon: 'fas fa-users-cog',              permission: 'users.view' },
  { path: '/admin/roles',      label: 'Roles & Permissions', icon: 'fas fa-user-shield',         permission: 'roles.view' },
  { path: '/admin/entities',   label: 'Sub-Entities',     icon: 'fas fa-building',               permission: 'entities.view' },
  { path: '/admin/currencies', label: 'Currencies',       icon: 'fas fa-coins',                  permission: 'currencies.view' },
  { path: '/admin/periods',    label: 'Fiscal Periods',   icon: 'fas fa-calendar-alt',           permission: 'periods.view' },

  { path: '/fees',             label: 'Fees Structure',   icon: 'fas fa-money-check' },
];

const Sidebar = ({ isOpen, isExpanded, onClose, onToggleExpand }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();

  const visibleItems = MENU_ITEMS.filter((item) => {
    if (item.divider) {
      return item.anyPermission ? item.anyPermission.some((p) => hasPermission(p)) : true;
    }
    return !item.permission || hasPermission(item.permission);
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const primaryRole = (user?.roles && user.roles[0]) || 'user';
  const displayRole = primaryRole
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? 'active' : ''}`} onClick={onClose}></div>
      <aside className={`sidebar ${isOpen ? 'open' : ''} ${isExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo" onClick={onToggleExpand} style={{ cursor: 'pointer' }}>
            <img src={elAlssonLogo} alt="El Alsson" className="sidebar-logo__img" />
          </div>
          <button className="sidebar-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <nav className="sidebar-nav">
          <ul>
            {visibleItems.map((item) =>
              item.divider ? (
                <li key={`divider-${item.label}`} className="sidebar-divider">
                  {isExpanded && <span className="sidebar-divider__label">{item.label}</span>}
                  {!isExpanded && <hr className="sidebar-divider__line" />}
                </li>
              ) : (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={location.pathname.startsWith(item.path) ? 'active' : ''}
                    onClick={onClose}
                    title={!isExpanded ? item.label : undefined}
                  >
                    <i className={item.icon}></i>
                    {isExpanded && <span>{item.label}</span>}
                  </Link>
                </li>
              )
            )}
          </ul>
        </nav>
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              <i className="fas fa-user-circle"></i>
            </div>
            {isExpanded && (
              <div className="user-details">
                <p className="user-name">{user?.fullName || user?.username || 'User'}</p>
                <p className="user-role">{displayRole}</p>
              </div>
            )}
          </div>
          {isExpanded ? (
            <button className="logout-btn" onClick={handleLogout}>
              <i className="fas fa-sign-out-alt"></i>
              Logout
            </button>
          ) : (
            <button
              className="logout-btn"
              onClick={handleLogout}
              title="Logout"
              style={{ justifyContent: 'center' }}
            >
              <i className="fas fa-sign-out-alt"></i>
            </button>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
